import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  FlaskConical, Users, CreditCard, Search, ChevronLeft, ChevronRight,
  ArrowLeft, Shield, Settings, Ban, CheckCircle, Pencil, Trash2, Plus, Eye
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SuperAdminLIS() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTabRaw] = useState(searchParams.get('tab') || 'subscribers');
  const setTab = (v) => { setTabRaw(v); setSearchParams(v === 'subscribers' ? {} : { tab: v }, { replace: true }); };

  const token = localStorage.getItem('super_admin_token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Subscribers state
  const [subs, setSubs] = useState([]);
  const [subTotal, setSubTotal] = useState(0);
  const [subPage, setSubPage] = useState(1);
  const [subSearch, setSubSearch] = useState('');
  const [subStatus, setSubStatus] = useState('');

  // Patients state
  const [patients, setPatients] = useState([]);
  const [patTotal, setPatTotal] = useState(0);
  const [patPage, setPatPage] = useState(1);
  const [patSearch, setPatSearch] = useState('');
  const [patVendor, setPatVendor] = useState('');

  // Plans state
  const [plans, setPlans] = useState([]);
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [editPlan, setEditPlan] = useState(null);
  const [planForm, setPlanForm] = useState({ name: '', billing_cycle: 'monthly', price: '', duration_days: '30', is_active: true });

  // Report upload vendors
  const [labVendors, setLabVendors] = useState([]);
  const [migrating, setMigrating] = useState(false);

  // Vendor edit dialog
  const [showVendorEdit, setShowVendorEdit] = useState(false);
  const [editVendorData, setEditVendorData] = useState(null);
  const [vendorForm, setVendorForm] = useState({ name: '', email: '', phone: '', address: '', gst: '' });

  useEffect(() => {
    if (!token) { navigate('/admin/login'); return; }
  }, [token, navigate]);

  // Fetch subscribers
  const fetchSubs = useCallback(async () => {
    const params = new URLSearchParams({ page: subPage, limit: 20 });
    if (subSearch) params.append('search', subSearch);
    if (subStatus) params.append('status', subStatus);
    const res = await fetch(`${API}/api/lis/subscription/admin/subscribers?${params}`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) { const d = await res.json(); setSubs(d.subscriptions || []); setSubTotal(d.total || 0); }
  }, [subPage, subSearch, subStatus, token]);

  // Fetch patients
  const fetchPatients = useCallback(async () => {
    const params = new URLSearchParams({ page: patPage, limit: 20 });
    if (patSearch) params.append('search', patSearch);
    if (patVendor) params.append('vendor_id', patVendor);
    const res = await fetch(`${API}/api/lis/subscription/admin/patients?${params}`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) { const d = await res.json(); setPatients(d.patients || []); setPatTotal(d.total || 0); }
  }, [patPage, patSearch, patVendor, token]);

  // Fetch plans
  const fetchPlans = useCallback(async () => {
    const res = await fetch(`${API}/api/lis/subscription/admin/plans`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) { const d = await res.json(); setPlans(d.plans || []); }
  }, [token]);

  // Fetch lab vendors for report upload toggle
  const fetchLabVendors = useCallback(async () => {
    const res = await fetch(`${API}/api/lis/subscription/admin/report-upload-status`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) { const d = await res.json(); setLabVendors(d.vendors || []); }
  }, [token]);

  useEffect(() => { if (tab === 'subscribers') fetchSubs(); }, [tab, fetchSubs]);
  useEffect(() => { if (tab === 'patients') fetchPatients(); }, [tab, fetchPatients]);
  useEffect(() => { if (tab === 'plans') fetchPlans(); }, [tab, fetchPlans]);
  useEffect(() => { if (tab === 'settings') fetchLabVendors(); }, [tab, fetchLabVendors]);

  // Subscription actions
  const blockSub = async (id) => {
    if (!window.confirm('Block this subscription?')) return;
    const res = await fetch(`${API}/api/lis/subscription/admin/subscribers/${id}/block`, { method: 'PUT', headers });
    if (res.ok) { toast.success('Blocked'); fetchSubs(); } else toast.error('Failed');
  };

  const unblockSub = async (id) => {
    const res = await fetch(`${API}/api/lis/subscription/admin/subscribers/${id}/unblock`, { method: 'PUT', headers, body: JSON.stringify({}) });
    if (res.ok) { toast.success('Unblocked'); fetchSubs(); } else toast.error('Failed');
  };

  // Plan CRUD
  const openCreatePlan = () => { setEditPlan(null); setPlanForm({ name: '', billing_cycle: 'monthly', price: '', duration_days: '30', is_active: true }); setShowPlanDialog(true); };
  const openEditPlan = (p) => { setEditPlan(p); setPlanForm({ name: p.name, billing_cycle: p.billing_cycle, price: String(p.price), duration_days: String(p.duration_days), is_active: p.is_active }); setShowPlanDialog(true); };

  const savePlan = async () => {
    const body = { ...planForm, price: parseFloat(planForm.price), duration_days: parseInt(planForm.duration_days) };
    const url = editPlan ? `${API}/api/lis/subscription/admin/plans/${editPlan.id}` : `${API}/api/lis/subscription/admin/plans`;
    const method = editPlan ? 'PUT' : 'POST';
    const res = await fetch(url, { method, headers, body: JSON.stringify(body) });
    if (res.ok) { toast.success(editPlan ? 'Updated' : 'Created'); setShowPlanDialog(false); fetchPlans(); } else toast.error('Failed');
  };

  const deletePlan = async (id) => {
    if (!window.confirm('Delete this plan?')) return;
    const res = await fetch(`${API}/api/lis/subscription/admin/plans/${id}`, { method: 'DELETE', headers });
    if (res.ok) { toast.success('Deleted'); fetchPlans(); } else toast.error('Failed');
  };

  // Report upload toggle
  const toggleReportUpload = async (vendorId, enabled) => {
    const res = await fetch(`${API}/api/lis/subscription/admin/vendors/${vendorId}/report-upload`, {
      method: 'PUT', headers, body: JSON.stringify({ enabled })
    });
    if (res.ok) { toast.success(enabled ? 'Report upload enabled' : 'Report upload disabled'); fetchLabVendors(); } else toast.error('Failed');
  };

  const runMigration = async (vendorId) => {
    if (!window.confirm('Import all data (tests, patients, doctors, bills) from the external Lab Report Pro system for this vendor?')) return;
    setMigrating(true);
    try {
      const res = await fetch(`${API}/api/lis/migrate/run`, {
        method: 'POST', headers, body: JSON.stringify({ vendor_id: vendorId })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Imported: ${data.tests || 0} tests, ${data.patients || 0} patients, ${data.doctors || 0} doctors, ${data.bills || 0} bills`);
      } else {
        toast.error(data.detail || 'Migration failed');
      }
    } catch (e) {
      toast.error('Migration failed');
    } finally {
      setMigrating(false);
    }
  };

  // Vendor edit
  const openVendorEdit = (v) => {
    setEditVendorData(v);
    setVendorForm({ name: v.name || '', email: v.email || '', phone: v.phone || '', address: v.address || '', gst: v.gst || '' });
    setShowVendorEdit(true);
  };

  const saveVendorDetails = async () => {
    const res = await fetch(`${API}/api/lis/subscription/admin/vendors/${editVendorData.id}/details`, {
      method: 'PUT', headers, body: JSON.stringify(vendorForm)
    });
    if (res.ok) { toast.success('Updated'); setShowVendorEdit(false); fetchLabVendors(); } else toast.error('Failed');
  };

  const subPages = Math.ceil(subTotal / 20);
  const patPages = Math.ceil(patTotal / 20);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="super-admin-lis">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin/dashboard" className="text-slate-400 hover:text-slate-600"><ArrowLeft className="w-5 h-5" /></Link>
            <FlaskConical className="w-5 h-5 text-blue-600" />
            <h1 className="text-base font-semibold text-slate-800">Lab Management Admin</h1>
          </div>
          <Badge variant="outline" className="text-xs"><Shield className="w-3 h-3 mr-1" /> Super Admin</Badge>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-4">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="subscribers" data-testid="tab-subscribers">Subscribers</TabsTrigger>
            <TabsTrigger value="patients" data-testid="tab-patients">LIS Patients</TabsTrigger>
            <TabsTrigger value="plans" data-testid="tab-plans">Plans & Pricing</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Report Settings</TabsTrigger>
          </TabsList>

          {/* Subscribers Tab */}
          <TabsContent value="subscribers">
            <Card className="border-slate-200 mb-4">
              <CardContent className="p-3">
                <div className="flex gap-3 flex-wrap">
                  <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
                    <Input placeholder="Search vendor name..." value={subSearch} onChange={e => { setSubSearch(e.target.value); setSubPage(1); }} className="pl-9 h-9" data-testid="sub-search" />
                  </div>
                  <Select value={subStatus} onValueChange={v => { setSubStatus(v === 'all' ? '' : v); setSubPage(1); }}>
                    <SelectTrigger className="w-[140px] h-9"><SelectValue placeholder="All Status" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="expired">Expired</SelectItem>
                      <SelectItem value="blocked">Blocked</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b bg-slate-50">
                    <th className="p-3 text-left font-medium text-slate-600">Vendor</th>
                    <th className="p-3 text-left font-medium text-slate-600">Plan</th>
                    <th className="p-3 text-left font-medium text-slate-600">Status</th>
                    <th className="p-3 text-left font-medium text-slate-600">Subscribed</th>
                    <th className="p-3 text-left font-medium text-slate-600">Expires</th>
                    <th className="p-3 text-right font-medium text-slate-600">Paid</th>
                    <th className="p-3 text-right font-medium text-slate-600 w-28">Actions</th>
                  </tr></thead>
                  <tbody>
                    {subs.map(s => (
                      <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="p-3"><p className="font-medium text-slate-800">{s.vendor_name}</p><p className="text-xs text-slate-400">{s.vendor_id?.slice(0, 8)}</p></td>
                        <td className="p-3 text-slate-600">{s.plan_name}</td>
                        <td className="p-3">
                          <Badge variant={s.status === 'active' ? 'default' : s.status === 'blocked' ? 'destructive' : 'secondary'} className="text-xs">
                            {s.status}
                          </Badge>
                        </td>
                        <td className="p-3 text-slate-500 text-xs">{s.starts_at?.slice(0, 10)}</td>
                        <td className="p-3 text-slate-500 text-xs">{s.expires_at?.slice(0, 10)}</td>
                        <td className="p-3 text-right font-medium">Rs.{s.price_paid}</td>
                        <td className="p-3 text-right">
                          {s.status === 'active' && (
                            <Button variant="outline" size="sm" onClick={() => blockSub(s.id)} className="h-7 text-xs text-red-600" data-testid={`block-sub-${s.id}`}>
                              <Ban className="w-3 h-3 mr-1" /> Block
                            </Button>
                          )}
                          {(s.status === 'blocked' || s.status === 'expired') && (
                            <Button variant="outline" size="sm" onClick={() => unblockSub(s.id)} className="h-7 text-xs text-green-600" data-testid={`unblock-sub-${s.id}`}>
                              <CheckCircle className="w-3 h-3 mr-1" /> Activate
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {subs.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-400">No subscriptions found.</td></tr>}
                  </tbody>
                </table>
              </div>
              {subPages > 1 && (
                <div className="flex items-center justify-between p-3 border-t">
                  <span className="text-xs text-slate-500">Total: {subTotal} | Page {subPage}/{subPages}</span>
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" disabled={subPage <= 1} onClick={() => setSubPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
                    <Button variant="outline" size="sm" disabled={subPage >= subPages} onClick={() => setSubPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
                  </div>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Patients Tab */}
          <TabsContent value="patients">
            <Card className="border-slate-200 mb-4">
              <CardContent className="p-3">
                <div className="flex gap-3 flex-wrap">
                  <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
                    <Input placeholder="Search name, mobile, ID..." value={patSearch} onChange={e => { setPatSearch(e.target.value); setPatPage(1); }} className="pl-9 h-9" data-testid="pat-search" />
                  </div>
                  <Input placeholder="Filter by Vendor ID" value={patVendor} onChange={e => { setPatVendor(e.target.value); setPatPage(1); }} className="w-[200px] h-9" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b bg-slate-50">
                    <th className="p-3 text-left font-medium text-slate-600">ID</th>
                    <th className="p-3 text-left font-medium text-slate-600">Name</th>
                    <th className="p-3 text-left font-medium text-slate-600">Mobile</th>
                    <th className="p-3 text-left font-medium text-slate-600">Sex</th>
                    <th className="p-3 text-left font-medium text-slate-600">Age</th>
                    <th className="p-3 text-left font-medium text-slate-600">Lab Vendor</th>
                    <th className="p-3 text-left font-medium text-slate-600">Registered</th>
                  </tr></thead>
                  <tbody>
                    {patients.map(p => (
                      <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="p-3 text-xs text-slate-500">#{p.patient_id}</td>
                        <td className="p-3 font-medium text-slate-800">{[p.title, p.first_name, p.last_name].filter(Boolean).join(' ')}</td>
                        <td className="p-3 text-slate-500">{p.mobile || '-'}</td>
                        <td className="p-3 text-slate-500">{p.sex || '-'}</td>
                        <td className="p-3 text-slate-500">{p.age_years ? `${p.age_years}y` : '-'}{p.age_months ? ` ${p.age_months}m` : ''}</td>
                        <td className="p-3"><Badge variant="outline" className="text-xs">{p.vendor_name || p.vendor_id?.slice(0, 8)}</Badge></td>
                        <td className="p-3 text-xs text-slate-500">{p.created_at?.slice(0, 10)}</td>
                      </tr>
                    ))}
                    {patients.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-400">No patients found.</td></tr>}
                  </tbody>
                </table>
              </div>
              {patPages > 1 && (
                <div className="flex items-center justify-between p-3 border-t">
                  <span className="text-xs text-slate-500">Total: {patTotal} | Page {patPage}/{patPages}</span>
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" disabled={patPage <= 1} onClick={() => setPatPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
                    <Button variant="outline" size="sm" disabled={patPage >= patPages} onClick={() => setPatPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
                  </div>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Plans Tab */}
          <TabsContent value="plans">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-slate-800">LIS Subscription Plans</h2>
              <Button size="sm" onClick={openCreatePlan} data-testid="create-plan-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add Plan</Button>
            </div>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {plans.map(p => (
                <Card key={p.id} className={`border-2 ${p.is_active ? 'border-slate-200' : 'border-red-200 opacity-60'}`}>
                  <CardContent className="p-5">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold text-slate-800">{p.name}</h3>
                        <Badge variant="outline" className="text-xs mt-1">{p.billing_cycle}</Badge>
                      </div>
                      {!p.is_active && <Badge variant="destructive" className="text-xs">Inactive</Badge>}
                    </div>
                    <p className="text-2xl font-bold text-slate-900 mb-1">Rs.{p.price?.toLocaleString()}</p>
                    <p className="text-xs text-slate-500 mb-4">{p.duration_days} days</p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => openEditPlan(p)} className="flex-1"><Pencil className="w-3 h-3 mr-1" /> Edit</Button>
                      <Button variant="outline" size="sm" onClick={() => deletePlan(p.id)} className="text-red-600"><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {plans.length === 0 && <p className="text-slate-400 text-sm col-span-3">No plans yet. Create one to get started.</p>}
            </div>
          </TabsContent>

          {/* Settings Tab - Report Upload & Vendor Management */}
          <TabsContent value="settings">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Lab Vendor Settings</h2>
            <Card className="border-slate-200">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b bg-slate-50">
                    <th className="p-3 text-left font-medium text-slate-600">Vendor Name</th>
                    <th className="p-3 text-center font-medium text-slate-600">Report Upload</th>
                    <th className="p-3 text-right font-medium text-slate-600 w-48">Actions</th>
                  </tr></thead>
                  <tbody>
                    {labVendors.map(v => (
                      <tr key={v.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="p-3 font-medium text-slate-800">{v.name}</td>
                        <td className="p-3 text-center">
                          <Switch
                            checked={v.report_upload_enabled || false}
                            onCheckedChange={(checked) => toggleReportUpload(v.id, checked)}
                            data-testid={`report-toggle-${v.id}`}
                          />
                        </td>
                        <td className="p-3 text-right">
                          <div className="flex gap-1 justify-end">
                            <Button variant="outline" size="sm" onClick={() => openVendorEdit(v)} className="h-7 text-xs">
                              <Pencil className="w-3 h-3 mr-1" /> Edit
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => runMigration(v.id)} disabled={migrating} className="h-7 text-xs text-blue-600" data-testid={`migrate-${v.id}`}>
                              {migrating ? 'Importing...' : 'Import Data'}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {labVendors.length === 0 && <tr><td colSpan={3} className="p-8 text-center text-slate-400">No lab vendors found.</td></tr>}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Plan Dialog */}
      <Dialog open={showPlanDialog} onOpenChange={setShowPlanDialog}>
        <DialogContent className="max-w-md" data-testid="plan-dialog">
          <DialogHeader><DialogTitle>{editPlan ? 'Edit Plan' : 'Create Plan'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Plan Name *" value={planForm.name} onChange={e => setPlanForm(f => ({ ...f, name: e.target.value }))} data-testid="plan-name-input" />
            <Select value={planForm.billing_cycle} onValueChange={v => setPlanForm(f => ({ ...f, billing_cycle: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="yearly">Yearly</SelectItem></SelectContent>
            </Select>
            <Input type="number" placeholder="Price (Rs.)" value={planForm.price} onChange={e => setPlanForm(f => ({ ...f, price: e.target.value }))} data-testid="plan-price-input" />
            <Input type="number" placeholder="Duration (days)" value={planForm.duration_days} onChange={e => setPlanForm(f => ({ ...f, duration_days: e.target.value }))} />
            <div className="flex items-center gap-2">
              <Switch checked={planForm.is_active} onCheckedChange={v => setPlanForm(f => ({ ...f, is_active: v }))} />
              <span className="text-sm text-slate-600">Active</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPlanDialog(false)}>Cancel</Button>
            <Button onClick={savePlan} data-testid="save-plan-btn">{editPlan ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vendor Edit Dialog */}
      <Dialog open={showVendorEdit} onOpenChange={setShowVendorEdit}>
        <DialogContent className="max-w-md" data-testid="vendor-edit-dialog">
          <DialogHeader><DialogTitle>Edit Vendor Details</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Name" value={vendorForm.name} onChange={e => setVendorForm(f => ({ ...f, name: e.target.value }))} />
            <Input placeholder="Email" value={vendorForm.email} onChange={e => setVendorForm(f => ({ ...f, email: e.target.value }))} />
            <Input placeholder="Phone" value={vendorForm.phone} onChange={e => setVendorForm(f => ({ ...f, phone: e.target.value }))} />
            <Input placeholder="Address" value={vendorForm.address} onChange={e => setVendorForm(f => ({ ...f, address: e.target.value }))} />
            <Input placeholder="GST" value={vendorForm.gst} onChange={e => setVendorForm(f => ({ ...f, gst: e.target.value }))} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVendorEdit(false)}>Cancel</Button>
            <Button onClick={saveVendorDetails} data-testid="save-vendor-btn">Update</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
